/* eslint-disable new-cap */
import { getOrganizationDisplayName } from 'utils/view';
import { isCollabarySupport } from 'utils/account';
import { getOrganizationRealType, isPlainBrandOrganization } from 'utils/organization';

import type { Account, Organization } from 'types';

class IntercomProvider {
  canTrack() {
    return (
      typeof window === 'object' &&
      'Intercom' in window &&
      Boolean(window.app.env.FEATURE_INTERCOM_TRACKING) &&
      Boolean(window.app.env.INTERCOM_APP_ID)
    );
  }

  prepareUserData(account, organization) {
    const organizationType = getOrganizationRealType(organization);

    const isCollabarySupportAccount = isCollabarySupport(account);
    const brandOrganizations = (account.organizations || [])
      .filter((o) => isPlainBrandOrganization(o) && !isCollabarySupportAccount);

    const intercomUser = {
      name: `${account.firstName} ${account.lastName}`, // Full name
      email: account.email, // required by Intercom
      user_id: account.id, // required by Intercom for secure mode
      user_hash: account.hash, // required by Intercom for secure mode
      organization_type: organizationType || 'none',
      organization_id: organization ? organization.id : 'none',
      created_at: account.signedUpAt, // required by Intercom
      hide_default_launcher: !organizationType,
      companies: brandOrganizations.map((o) => ({
        id: o.id, // required by Intercom
        name: getOrganizationDisplayName(o) // required by Intercom
      }))
    };

    return intercomUser;
  }

  initialize() {
    if (!this.canTrack()) {
      return;
    }

    /* From Intercom docs:
     If your user is already logged-in when the page loads,
     add the user data to the boot function call.
     */
    window.Intercom('boot', {
      app_id: window.app.env.INTERCOM_APP_ID
    });
  }

  trackLogin(email) {
    if (!this.canTrack()) {
      return;
    }

    /* From Intercom docs:
     To send user data to Intercom later,
     i.e. when a visitor signs up or when a logged-out user signs in,
     call the update function with the user data.
     */
    window.Intercom('update', {
      email
    });
  }

  trackSignUp(createdOrganization) {
    if (!this.canTrack()) {
      return;
    }

    const createdUser = createdOrganization.members[0];

    window.Intercom('update', {
      ...this.prepareUserData(createdUser, createdOrganization),
      organization_subscription: createdOrganization.subscriptionType || 'none'
    });
  }

  trackAccountDataUpdated(account: Account, organization?: Organization) {
    if (!this.canTrack() || !account) {
      return;
    }

    window.Intercom('update', this.prepareUserData(account, organization));
  }

  trackOrganizationSwitch(account, organization) {
    if (!this.canTrack() || !account) {
      return;
    }

    window.Intercom('update', this.prepareUserData(account, organization));
  }

  trackCampaignSwitch(account, organization, campaignId) {
    if (!this.canTrack() || !account || !campaignId) {
      return;
    }

    const preparedUserData = this.prepareUserData(account, organization);
    const enrichedUserData = {
      ...preparedUserData,
      campaign_id: campaignId
    };

    window.Intercom('update', enrichedUserData);
  }

  trackEvent(eventName, meta) {
    if (!this.canTrack()) {
      return;
    }

    window.Intercom('trackEvent', eventName, meta);
  }

  trackPageView() {
    if (!this.canTrack()) {
      return;
    }

    /* From Intercom docs:
     Whenever the view and/or URL changes in your app, you should send an update call to Intercom.
     This ensures that your visitors receive the auto messages that are most relevant to
     their latest view and behavior.
     */
    window.Intercom('update');
  }

  trackLogout() {
    if (!this.canTrack()) {
      return;
    }

    /* From Intercom docs:
     Finally, when a user logs out of your app,
     make a shutdown call to notify Intercom and reset the Messenger.
     */
    window.Intercom('shutdown');

    this.initialize(); // run `boot` again
  }
}

export default new IntercomProvider();
